import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, Inject, PLATFORM_ID, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { DocumentThemeService } from './core/services/document-theme.service';
import { RaygunService } from './core/services/raygun.service';
import { AppConfigService } from '../app/core/services/app-config.service';
import { WindowAccessor } from './core/accessors/window.accessor';
import { debounceTime, distinctUntilChanged, filter, Observable } from 'rxjs';
import { BaseComponent } from './core/components/base.component';
import WindowMessageHelper from './core/helpers/window-message-helper';
import { NavigationEnd, Router } from '@angular/router';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent extends BaseComponent {
  constructor(
    private readonly documentThemeService: DocumentThemeService,
    private readonly raygunService: RaygunService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly appConfigService: AppConfigService,
    private readonly windowAccessor: WindowAccessor,
    private readonly router: Router) {
    super();
  }

  ngOnInit(): void {
    // On server render the custom theme style attribures
    if (isPlatformServer(this.platformId)) {
      this.raygunService.apply();
    }

    const source = this.getSource();
    // Fetch and apply the theme based on the current source
    if (source) {
      this.documentThemeService.apply(source);
    }

    if (isPlatformBrowser(this.platformId)) {
      this.setupHeightMutationObserver();

      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .unsubscribeOnDestroy(this)
        .subscribe(() => {
          WindowMessageHelper.sendNavigation(this.windowAccessor);
        });
    }
  }

  setupHeightMutationObserver() {
    const window = this.windowAccessor.get();

    if (!window) {
      return;
    }

    // Observe the approot html element for height changes
    const appRoot = document.getElementsByTagName('app-root')[0];

    new Observable<number>(observer => {
      // Callback function to execute when mutations are observed
      // this can and will be called very often
      const callback = () => {
        const height = (appRoot as any).offsetHeight;

        if (height) {
          observer.next(height);
        }
      };

      // Create an observer instance linked to the callback function
      const elementObserver = new MutationObserver(callback);

      // Options for the observer (which mutations to observe)
      const config = {
        attributes: true,
        childList: true,
        subtree: true
      };

      // Start observing the target node for configured mutations
      elementObserver.observe(appRoot, config);
    }).pipe(
      debounceTime(50), // wait until 50 milliseconds have lapsed since the observable was last sent
      distinctUntilChanged() // if the value hasn't changed, don't continue
    )
      .unsubscribeOnDestroy(this)
      .subscribe((newHeight => {
        WindowMessageHelper.sendHeight(this.windowAccessor, newHeight);
      }));
  }

  private getSource(): string | null {
    const fragments = this.document.URL
      .replace('//', '')
      .split('/');

    if (fragments.length > 1) {
      return fragments[1];
    }

    return null;
  }
}
